import * as React from 'react';
import { cn } from '@/lib/cn';

const RadioGroupContext = React.createContext<{
    value: string;
    name: string;
    onValueChange: (value: string) => void;
} | null>(null);

export interface RadioGroupProps {
    value: string;
    onValueChange: (value: string) => void;
    children: React.ReactNode;
    className?: string;
}

export function RadioGroup({ value, onValueChange, children, className }: RadioGroupProps): React.ReactElement {
    const name = React.useId();
    return (
        <RadioGroupContext.Provider value={{ value, name, onValueChange }}>
            <div className={cn('flex flex-col gap-2', className)} role="radiogroup">
                {children}
            </div>
        </RadioGroupContext.Provider>
    );
}

export interface RadioGroupItemProps {
    value: string;
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
}

export function RadioGroupItem({ value, children, className, disabled }: RadioGroupItemProps): React.ReactElement {
    const context = React.useContext(RadioGroupContext);
    if (!context) {
        throw new Error('RadioGroupItem must be used within RadioGroup');
    }
    const { value: groupValue, name, onValueChange } = context;
    const checked = groupValue === value;
    return (
        <label
            className={cn(
                'flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700',
                disabled && 'cursor-not-allowed opacity-50',
                className
            )}
        >
            <input
                type="radio"
                name={name}
                value={value}
                checked={checked}
                onChange={() => onValueChange(value)}
                disabled={disabled}
                className="h-4 w-4 rounded-full border-gray-300 text-brand-600 focus:ring-2 focus:ring-brand-500 focus:ring-offset-0"
            />
            {children}
        </label>
    );
}
